// import node module libraries
import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Col, Form, Row, Table } from 'react-bootstrap';
import {
	useFilters,
	useGlobalFilter,
	usePagination,
	useTable
} from 'react-table';
import { FormSelect } from 'components/elements/form-select/FormSelect';

// import custom components
import GlobalFilter from 'components/elements/advance-table/GlobalFilter';
import Pagination from 'components/elements/advance-table/Pagination';

// import utility file
import BudgetService from '../../services/BudgetService';
import AnneescolaireService from '../../services/AnneescolaireService';

const BudgetListItems = () => {
	const [originalBudgets, setOriginalBudgets] = useState([]);
	const [budgets, setBudgets] = useState([]);
	const [anneescolaire, setAnneescolaire] = useState([]); // Add this line
	const [yearOptions, setYearOptions] = useState([]);

	useEffect(() => {
		const fetchYearOptions = async () => {
			try {
				const years = await AnneescolaireService.getYearOptions();
				setYearOptions(years);
			} catch (error) {
				console.error('Error fetching years options', error);
			}
		};

		fetchYearOptions();
	}, []);

	const YEAR_CHOICES = yearOptions;

	useEffect(() => {
		Promise.all([
			BudgetService.getAllBudgets(),
			AnneescolaireService.getAllAnneescolaire()
		]).then(([budgetsData, anneescolaireData]) => {
			setOriginalBudgets(budgetsData);
			setBudgets(budgetsData);
			setAnneescolaire(anneescolaireData);
		});
	}, []);

	const getFilterTerm = (event) => {
		let filterTerm = event.target.value;
		if (filterTerm !== '') {
			const newBudgetsList = originalBudgets.filter((budget) => {
				return budget.anneescolaire === Number(filterTerm);
			});
			setBudgets(newBudgetsList);
		} else {
			setBudgets(originalBudgets);
		}
	};

	const formatCategorieBudget = (value) => {
		return value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
	};

	const getAnneescolaireById = (anneescolaireId) => {
		const anneeScolaireObj =
			anneescolaire.find((item) => item.id === anneescolaireId) ?? {};

		return anneeScolaireObj
			? anneeScolaireObj.annee_debut + ' - ' + anneeScolaireObj.annee_fin
			: '';
	};

	const formatValue = (value, columnId) => {
		if (columnId === 'prevision_depense' || columnId === 'prevision_recette') {
			return new Intl.NumberFormat('fr-FR', {
				style: 'currency',
				currency: 'XAF'
			}).format(value);
		}
		return value;
	};

	const columns = useMemo(
		() => [
			{ accessor: 'id', Header: 'ID', show: false },
			{
				accessor: 'categorie',
				Header: 'Catégorie',
				Cell: ({ value, row }) => {
					return (
						<div>
							{value ? (
								<Link to={`/finances/budget/${row.original.id}`}>
									<h5 className="mb-0 ms-2">
										{formatCategorieBudget(value, 'categorie')}
									</h5>
								</Link>
							) : null}
						</div>
					);
				}
			},

			{
				accessor: 'prevision_depense',
				Header: 'Prévision dépenses',
				Cell: ({ value }) => (
					<div>{formatValue(value, 'prevision_depense')}</div>
				)
			},
			{
				accessor: 'prevision_recette',
				Header: 'Prévision recettes',
				Cell: ({ value }) => (
					<div>{formatValue(value, 'prevision_recette')}</div>
				)
			},

			{
				accessor: 'anneescolaire',
				Header: 'Année scolaire',
				Cell: ({ value }) => <div>{value}</div>
			}
		],
		[]
	);

	const data = useMemo(() => {
		return budgets.map((budget) => ({
			...budget,
			anneescolaire: getAnneescolaireById(budget.anneescolaire)
		}));
	}, [budgets]);

	const {
		getTableProps,
		getTableBodyProps,
		headerGroups,
		page,
		nextPage,
		previousPage,
		state,
		gotoPage,
		pageCount,
		prepareRow,
		setGlobalFilter
	} = useTable(
		{
			columns,
			data,
			initialState: {
				pageSize: 10,
				hiddenColumns: columns.map((column) => {
					if (column.show === false) return column.accessor || column.id;
					else return false;
				})
			}
		},
		useFilters,
		useGlobalFilter,
		usePagination
	);

	const { pageIndex, globalFilter } = state;

	return (
		<Fragment>
			<div className="overflow-hidden p-3">
				<Row className="justify-content-md-between mb-4 mb-xl-0 ">
					<Col xxl={3} lg={4} md={6} xs={12}>
						<GlobalFilter
							filter={globalFilter}
							setFilter={setGlobalFilter}
							placeholder="Rechercher par catégorie"
						/>
					</Col>
					<Col xxl={3} lg={4} md={6} xs={12}>
						{/* records filtering options */}
						<Form.Control
							as={FormSelect}
							placeholder="Filtrer par année scolaire"
							options={YEAR_CHOICES}
							onChange={getFilterTerm}
						/>
					</Col>
				</Row>
			</div>

			<Table
				hover
				responsive
				{...getTableProps()}
				className="table text-nowrap table-centered"
			>
				<thead className="table-light">
					{headerGroups.map((headerGroup) => (
						<tr {...headerGroup.getHeaderGroupProps()}>
							{headerGroup.headers.map((column) => (
								<th {...column.getHeaderProps()}>{column.render('Header')}</th>
							))}
						</tr>
					))}
				</thead>
				<tbody {...getTableBodyProps()}>
					{page.map((row) => {
						prepareRow(row);
						return (
							<tr {...row.getRowProps()}>
								{row.cells.map((cell) => {
									return (
										<td {...cell.getCellProps()}>{cell.render('Cell')}</td>
									);
								})}
							</tr>
						);
					})}
				</tbody>
			</Table>

			<Pagination
				previousPage={previousPage}
				pageCount={pageCount}
				pageIndex={pageIndex}
				gotoPage={gotoPage}
				nextPage={nextPage}
			/>
		</Fragment>
	);
};

export default BudgetListItems;
